import React, { useMemo, useRef } from 'react';
import type { LayoutItem } from 'react-grid-layout';
import {
  CHART_LAYOUT_DEFAULTS,
  DEFAULT_CHART_LAYOUT,
  GRID_COLS,
  GRID_ROW_HEIGHT,
  GRID_MARGIN,
  GRID_CONTAINER_PADDING,
} from '../../grid/types';
import ChartSkeleton from './ChartSkeleton';
import type { ReadyChart } from '../../../api/types';

// ─── Skeleton slot definition ───────────────────────────────────────────────

interface SkeletonSlot {
  id: string;
  chartType: string;
  title?: string;
  w?: number;
  h?: number;
}

/**
 * Default skeleton layout used before the plan preview arrives.
 * Includes KPI cards (top row), then mixed chart types below.
 */
const DEFAULT_SLOTS: SkeletonSlot[] = [
  { id: 'sk-kpi-1', chartType: 'kpi' },
  { id: 'sk-kpi-2', chartType: 'kpi' },
  { id: 'sk-kpi-3', chartType: 'kpi' },
  { id: 'sk-kpi-4', chartType: 'kpi' },
  { id: 'sk-line-1', chartType: 'line' },
  { id: 'sk-bar-1', chartType: 'bar' },
  { id: 'sk-pie-1', chartType: 'pie' },
  { id: 'sk-table-1', chartType: 'table' },
];

/**
 * Build a grid layout from slots, filling rows left-to-right.
 */
function buildGridLayout(slots: SkeletonSlot[]): LayoutItem[] {
  const layout: LayoutItem[] = [];
  let currentX = 0;
  let currentY = 0;
  let rowMaxH = 0;

  for (const slot of slots) {
    const defaults = CHART_LAYOUT_DEFAULTS[slot.chartType] ?? DEFAULT_CHART_LAYOUT;
    const w = slot.w ?? defaults.w;
    const h = slot.h ?? defaults.h;

    if (currentX + w > GRID_COLS) {
      currentX = 0;
      currentY += rowMaxH;
      rowMaxH = 0;
    }

    layout.push({
      i: slot.id,
      x: currentX,
      y: currentY,
      w,
      h,
      minH: defaults.minH,
      maxH: defaults.maxH,
      minW: defaults.minW,
      static: true,
    });

    currentX += w;
    rowMaxH = Math.max(rowMaxH, h);
  }

  return layout;
}

// ─── Component ──────────────────────────────────────────────────────────────

interface DashboardLayoutSkeletonProps {
  containerWidth?: number;
  readyCharts?: ReadyChart[];
  /** When true, renders static placeholders with no animations or shimmer. */
  frozen?: boolean;
}

/**
 * Renders the skeleton grid layout.
 *
 * Two phases:
 * 1. **Skeleton phase** (before readyCharts arrive):
 *    Shows shimmer skeletons using plan preview types or default slots.
 *
 * 2. **Build phase** (readyCharts start arriving):
 *    Clears skeletons and progressively adds confirmed chart cards.
 *    New charts appear at top-left, pushing existing ones right/down.
 *    Each new card fades in.
 */
const DashboardLayoutSkeleton: React.FC<DashboardLayoutSkeletonProps> = ({
  containerWidth,
  readyCharts,
  frozen = false,
}) => {
  // Track which chart IDs we've already seen (for fade-in on new arrivals)
  const seenChartsRef = useRef<Set<string>>(new Set());

  const hasReadyCharts = readyCharts && readyCharts.length > 0;

  // Reset seen charts when transitioning back to skeleton phase
  if (!hasReadyCharts && seenChartsRef.current.size > 0) {
    seenChartsRef.current.clear();
  }

  // ── Skeleton phase: show shimmer skeletons ────────────────────────────
  const skeletonSlots = useMemo(() => DEFAULT_SLOTS, []);

  const skeletonLayout = useMemo(() => buildGridLayout(skeletonSlots), [skeletonSlots]);

  // ── Build phase: ready charts as slots ────────────────────────────────
  // KPIs first (top row), then other charts in arrival order.
  // Within each group, newest chart appears first (top-left).
  const buildSlots = useMemo(() => {
    if (!readyCharts || readyCharts.length === 0) return [];

    const kpis = readyCharts.filter((rc) => rc.type === 'kpi' || rc.type === 'kpi_wide');
    const others = readyCharts.filter((rc) => rc.type !== 'kpi' && rc.type !== 'kpi_wide');

    // KPIs in arrival order (top row), others newest-first
    const ordered = [...kpis, ...[...others].reverse()];

    return ordered.map((rc) => ({
      id: `ready-${rc.chartId || rc.index}`,
      chartType: rc.type,
      title: rc.title,
    }));
  }, [readyCharts]);

  const buildLayout = useMemo(() => buildGridLayout(buildSlots), [buildSlots]);

  // Determine which cards are newly arrived (for fade-in)
  const newCardIds = useMemo(() => {
    if (!readyCharts) return new Set<string>();
    const newIds = new Set<string>();
    for (const rc of readyCharts) {
      const id = `ready-${rc.chartId || rc.index}`;
      if (!seenChartsRef.current.has(id)) {
        newIds.add(id);
        seenChartsRef.current.add(id);
      }
    }
    return newIds;
  }, [readyCharts]);

  // Pick which mode to render
  const activeSlots = hasReadyCharts ? buildSlots : skeletonSlots;
  const activeLayout = hasReadyCharts ? buildLayout : skeletonLayout;

  const slotMap = useMemo(
    () => new Map(activeSlots.map((s) => [s.id, s])),
    [activeSlots],
  );

  // Calculate total grid height
  const totalHeight = useMemo(() => {
    let maxBottom = 0;
    for (const item of activeLayout) {
      const bottom = item.y + item.h;
      if (bottom > maxBottom) maxBottom = bottom;
    }
    return maxBottom * GRID_ROW_HEIGHT + (maxBottom > 0 ? (maxBottom - 1) * GRID_MARGIN[1] : 0);
  }, [activeLayout]);

  const getItemStyle = (item: LayoutItem): React.CSSProperties => {
    const [marginX, marginY] = GRID_MARGIN;
    const [padX] = GRID_CONTAINER_PADDING;

    if (containerWidth) {
      const usableWidth = containerWidth - 2 * padX;
      const colWidth = (usableWidth - (GRID_COLS - 1) * marginX) / GRID_COLS;

      return {
        position: 'absolute',
        left: padX + item.x * (colWidth + marginX),
        top: item.y * (GRID_ROW_HEIGHT + marginY),
        width: item.w * colWidth + (item.w - 1) * marginX,
        height: item.h * GRID_ROW_HEIGHT + (item.h - 1) * marginY,
      };
    }

    const leftPct = (item.x / GRID_COLS) * 100;
    const widthPct = (item.w / GRID_COLS) * 100;

    return {
      position: 'absolute',
      left: `${leftPct}%`,
      top: item.y * (GRID_ROW_HEIGHT + marginY),
      width: `calc(${widthPct}% - ${marginX}px)`,
      height: item.h * GRID_ROW_HEIGHT + (item.h - 1) * marginY,
    };
  };

  return (
    <div
      className="dashboard-layout-skeleton"
      style={{ position: 'relative', width: '100%', height: totalHeight }}
    >
      {activeLayout.map((item) => {
        const slot = slotMap.get(item.i);
        if (!slot) return null;

        const isInBuildPhase = hasReadyCharts;
        const isNewCard = newCardIds.has(item.i);

        return (
          <div
            key={item.i}
            style={{
              ...getItemStyle(item),
              // Smooth position transitions when cards shift
              transition: isInBuildPhase && !frozen ? 'left 400ms ease, top 400ms ease' : undefined,
            }}
            className={[
              isNewCard && !frozen ? 'chart-card--entering' : undefined,
              frozen ? 'skeleton-chart--frozen' : undefined,
            ].filter(Boolean).join(' ') || undefined}
          >
            {isInBuildPhase && !frozen ? (
              // Build phase: confirmed card with title + type icon
              <ChartSkeleton
                type={slot.chartType}
                title={slot.title}
                isReady
              />
            ) : (
              // Skeleton phase: animated shimmer (or frozen static)
              <ChartSkeleton
                type={slot.chartType}
                title={slot.title}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(DashboardLayoutSkeleton);